import {
  isAccountStatusId,
  isCompanyId,
  isCompliancePolicyId,
  isGameParticipantId,
  isProgramId,
  isReportConfigId,
  isProgramTaskCriteriaType,
  isRatioId,
  isRoleId,
  isTaskStatus,
  isTeamId,
  isUserId,
  isWebhookId,
} from '@terros/common'
import { DateTime } from 'luxon'
import { createParser, type ParserBuilder } from 'nuqs'
import { z } from 'zod'

export function parseAsType<T extends string>(isType: (type: string | T) => type is T): ParserBuilder<T> {
  return createParser({
    parse(queryValue) {
      const decoded = decodeURIComponent(queryValue)
      if (isType(decoded)) {
        return decoded
      }
      return null
    },
    serialize(value) {
      return encodeURIComponent(value)
    },
  })
}

export function parseAsTerrosId<T extends string>(isId: (id: string | T) => id is T): ParserBuilder<T> {
  return parseAsType(isId)
}

export const parseAsAccountStatusId = parseAsType(isAccountStatusId)
export const parseAsCompanyId = parseAsType(isCompanyId)
export const parseAsCompliancePolicyId = parseAsType(isCompliancePolicyId)
export const parseAsParticipantId = parseAsType(isGameParticipantId)
export const parseAsRatioId = parseAsType(isRatioId)
export const parseAsRoleId = parseAsType(isRoleId)
export const parseAsTeamId = parseAsType(isTeamId)
export const parseAsUserId = parseAsType(isUserId)
export const parseAsWebhookId = parseAsType(isWebhookId)
export const parseAsProgramId = parseAsType(isProgramId)
export const parseAsProgramTaskCriteriaType = parseAsType(isProgramTaskCriteriaType)
export const parseAsReportConfigId = parseAsType(isReportConfigId)
export const parseAsTaskStatus = parseAsType(isTaskStatus)

export function parseAsZodSchema<T extends z.ZodSchema>(schema: T): ParserBuilder<z.infer<T>> {
  return createParser({
    parse(queryValue) {
      try {
        const decoded = decodeURIComponent(queryValue)
        const parsed = JSON.parse(decoded)
        const result = schema.safeParse(parsed)
        return result.success ? result.data : null
      } catch {
        return null
      }
    },
    serialize(value) {
      return encodeURIComponent(JSON.stringify(value))
    },
  })
}

// Free-text search is JSON-encoded so TanStack Router's JSON.parse of search values can't coerce it (e.g. '123 ' -> 123)
export const parseAsSearchQuery: ParserBuilder<string> = parseAsZodSchema(z.string())

export const parseAsLuxonDateTime: ParserBuilder<DateTime> = createParser({
  parse(queryValue) {
    const decoded = decodeURIComponent(queryValue)
    const dateTime = DateTime.fromISO(decoded)
    return dateTime.isValid ? dateTime : null
  },
  serialize(value) {
    return encodeURIComponent(value.toISO())
  },
})
